import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';

@Injectable({
  providedIn: 'root'
})
export class BusinessesService {

  constructor(private firestore: AngularFirestore) { }

  /**
   * Fetches business data from Firestore using the IDs stored in localStorage
   * @returns Promise<Array<{id: string, name: string}>>
   */
  async getManagedBusinesses(): Promise<Array<{id: string, name: string}>> {
    try {
      // Check if we have cached data
      const cachedData = this.getCachedBusinesses();
      if (cachedData) {
        return cachedData;
      }

      // Get business IDs from localStorage
      const mbRaw = localStorage.getItem('mb');
      if (!mbRaw) {
        console.warn('No managed business IDs found in localStorage');
        return [];
      }

      const businessIds = JSON.parse(mbRaw);
      if (!Array.isArray(businessIds) || businessIds.length === 0) {
        console.warn('No valid business IDs found');
        return [];
      }

      // Fetch businesses from Firestore
      const db = getFirestore();
      const businessesRef = collection(db, 'Businesses');
      const q = query(businessesRef, where('__name__', 'in', businessIds));

      const snapshot = await getDocs(q);
      const businesses = snapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data()['name'] || doc.id // Fallback to ID if name is not available
      }));

      // Cache the data
      this.cacheBusinesses(businesses);

      return businesses;
    } catch (error) {
      console.error('Error fetching managed businesses:', error);
      // Return cached data if available
      const cachedData = this.getCachedBusinesses();
      return cachedData || [];
    }
  }

  /**
   * Gets cached business data from localStorage
   */
  private getCachedBusinesses(): Array<{id: string, name: string}> | null {
    try {
      const cached = localStorage.getItem('managedBusinesses');
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Error parsing cached business data:', error);
      return null;
    }
  }

  /**
   * Caches business data in localStorage
   */
  private cacheBusinesses(businesses: Array<{id: string, name: string}>): void {
    try {
      localStorage.setItem('managedBusinesses', JSON.stringify(businesses));
    } catch (error) {
      console.error('Error caching business data:', error);
    }
  }
}
